import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';

import { getChoiceOptionLabels } from './aiBuilderMessageStream';
import { createAiBuilderClientRequestId } from './aiBuilderTurnRequests';
import {
  type AiBuilderConfirmDecision,
  type AiBuilderConfirmOptionStyle,
  type AiBuilderMessage,
  type AiBuilderMessageAttachment,
  type AiBuilderUserMessageRetryRequest,
} from './types';

/** 문제 카드 `AI로 생성` 진입에서 띄우는 로컬 후속 선택지의 슬롯 식별자. */
export const QUIZ_CARD_ACTION_CHOICE_SLOT = 'quiz_card_action';

export type AiBuilderConfirmChoiceIntent = {
  token: string;
  decision: AiBuilderConfirmDecision;
  style: AiBuilderConfirmOptionStyle;
  optionId: string;
  label: string;
};

export type AiBuilderConfirmMessageIntent = {
  token: string;
  decision: AiBuilderConfirmDecision;
  label: string;
};

export const clearAiBuilderAssistantActions = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] => {
  let changed = false;

  const nextMessages = messages.map(message => {
    if (message.kind !== 'assistant' || !message.actions?.length) {
      return message;
    }

    changed = true;
    return {
      ...message,
      actions: undefined,
    };
  });

  return changed ? nextMessages : messages;
};

export const clearAiBuilderAssistantJumpLinks = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] => {
  let changed = false;

  const nextMessages = messages.map(message => {
    if (message.kind !== 'assistant' || !message.jumpLinks?.length) {
      return message;
    }

    changed = true;
    return {
      ...message,
      jumpLinks: undefined,
    };
  });

  return changed ? nextMessages : messages;
};

export const clearAiBuilderAssistantFollowupControls = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] => {
  return clearAiBuilderAssistantJumpLinks(
    clearAiBuilderAssistantActions(messages),
  );
};

export const createAiBuilderUserMessageId = (requestId: number) =>
  `user-${requestId}`;

export const createSubmittedAiBuilderChoiceUserMessageId = (
  requestId: number,
) => `user-choice-${requestId}`;

export const createSubmittedAiBuilderConfirmUserMessageId = (
  requestId: number,
) => `user-confirm-${requestId}`;

export const appendAiBuilderUserMessage = ({
  messages,
  attachment,
  consumeChoiceMessageId,
  content,
  requestId,
  retry,
  status = 'sending',
}: {
  messages: AiBuilderMessage[];
  attachment?: AiBuilderMessageAttachment;
  consumeChoiceMessageId?: string;
  content: string;
  requestId: number;
  retry?: AiBuilderUserMessageRetryRequest;
  status?: Extract<AiBuilderMessage, { kind: 'user' }>['status'];
}): AiBuilderMessage[] => {
  return [
    ...clearAiBuilderAssistantFollowupControls(messages).map(message => {
      if (
        consumeChoiceMessageId &&
        message.id === consumeChoiceMessageId &&
        message.kind === 'choice'
      ) {
        return {
          ...message,
          submitted: true,
        };
      }

      return message;
    }),
    {
      id: createAiBuilderUserMessageId(requestId),
      kind: 'user',
      content,
      attachment,
      status,
      ...(retry ? { retry } : {}),
    },
  ];
};

export const updateAiBuilderUserMessageStatus = ({
  messages,
  messageId,
  status,
}: {
  messages: AiBuilderMessage[];
  messageId: string;
  status: Extract<AiBuilderMessage, { kind: 'user' }>['status'];
}): AiBuilderMessage[] => {
  let changed = false;

  const nextMessages = messages.map(message => {
    if (message.kind !== 'user' || message.id !== messageId) {
      return message;
    }

    if (message.status === status) {
      return message;
    }

    changed = true;
    return {
      ...message,
      status,
    };
  });

  return changed ? nextMessages : messages;
};

/**
 * 실패한 유저 메시지를 재실행할 때, 해당 턴에서 남은 오류 안내(system/error)를 제거한다.
 * 재실행 대상 유저 메시지와 그 다음 유저 메시지 사이의 error 톤 system 메시지만 지우므로,
 * 재실행과 무관한 이전/이후 다른 실패 턴의 오류 안내는 그대로 유지된다.
 */
export const clearAiBuilderTurnErrorMessages = ({
  messages,
  userMessageId,
}: {
  messages: AiBuilderMessage[];
  userMessageId: string;
}): AiBuilderMessage[] => {
  const userMessageIndex = messages.findIndex(
    message => message.kind === 'user' && message.id === userMessageId,
  );

  if (userMessageIndex === -1) {
    return messages;
  }

  const nextUserIndexOffset = messages
    .slice(userMessageIndex + 1)
    .findIndex(message => message.kind === 'user');
  const nextUserMessageIndex =
    nextUserIndexOffset === -1
      ? messages.length
      : userMessageIndex + 1 + nextUserIndexOffset;

  let changed = false;

  const nextMessages = messages.filter((message, index) => {
    const isTurnErrorMessage =
      index > userMessageIndex &&
      index < nextUserMessageIndex &&
      message.kind === 'system' &&
      message.tone === 'error';

    if (isTurnErrorMessage) {
      changed = true;
      return false;
    }

    return true;
  });

  return changed ? nextMessages : messages;
};

export const settleSendingAiBuilderUserMessages = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] => {
  let changed = false;

  const nextMessages = messages.map(message => {
    if (message.kind !== 'user' || message.status !== 'sending') {
      return message;
    }

    changed = true;
    return {
      ...message,
      status: 'sent' as const,
    };
  });

  return changed ? nextMessages : messages;
};

export const getSubmittedAiBuilderChoiceLabels = ({
  messages,
  messageId,
}: {
  messages: AiBuilderMessage[];
  messageId: string;
}): string[] | null => {
  const targetMessage = messages.find(message => message.id === messageId);

  if (
    !targetMessage ||
    targetMessage.kind !== 'choice' ||
    targetMessage.submitted ||
    targetMessage.selectedOptionIds.length === 0
  ) {
    return null;
  }

  return getChoiceOptionLabels(
    targetMessage.options,
    targetMessage.selectedOptionIds,
  );
};

export const getSubmittedAiBuilderConfirmChoiceIntent = ({
  messages,
  messageId,
}: {
  messages: AiBuilderMessage[];
  messageId: string;
}): AiBuilderConfirmChoiceIntent | null => {
  const targetMessage = messages.find(message => message.id === messageId);

  if (
    !targetMessage ||
    targetMessage.kind !== 'choice' ||
    targetMessage.submitted ||
    !targetMessage.confirm ||
    targetMessage.selectedOptionIds.length !== 1
  ) {
    return null;
  }

  const selectedOptionId = targetMessage.selectedOptionIds[0];
  const selectedOption = targetMessage.options.find(
    option => option.id === selectedOptionId,
  );

  if (!selectedOption?.confirm) {
    return null;
  }

  return {
    token: targetMessage.confirm.token,
    decision: selectedOption.confirm.decision,
    style: selectedOption.confirm.style,
    optionId: selectedOption.id,
    label: selectedOption.label,
  };
};

export const getAiBuilderConfirmMessageIntent = ({
  decision,
  messages,
  messageId,
}: {
  decision: AiBuilderConfirmDecision;
  messages: AiBuilderMessage[];
  messageId: string;
}): AiBuilderConfirmMessageIntent | null => {
  const targetMessage = messages.find(message => message.id === messageId);

  if (
    !targetMessage ||
    targetMessage.kind !== 'confirm' ||
    targetMessage.submitted
  ) {
    return null;
  }

  return {
    token: targetMessage.token,
    decision,
    label:
      decision === 'approve'
        ? targetMessage.approveLabel
        : targetMessage.cancelLabel,
  };
};

export const appendSubmittedAiBuilderChoiceUserMessage = ({
  messages,
  messageId,
  responseText,
  requestId,
  retry,
  selectedLabels,
  status = 'sending',
}: {
  messages: AiBuilderMessage[];
  messageId: string;
  responseText?: string;
  requestId: number;
  retry?: AiBuilderUserMessageRetryRequest;
  selectedLabels: string[];
  status?: Extract<AiBuilderMessage, { kind: 'user' }>['status'];
}): AiBuilderMessage[] => {
  const content = [selectedLabels.join(', '), responseText?.trim()]
    .filter(Boolean)
    .join('\n');

  return [
    ...clearAiBuilderAssistantFollowupControls(messages).map(message =>
      message.id === messageId && message.kind === 'choice'
        ? { ...message, submitted: true }
        : message,
    ),
    {
      id: createSubmittedAiBuilderChoiceUserMessageId(requestId),
      kind: 'user',
      content,
      status,
      ...(retry ? { retry } : {}),
    },
  ];
};

export const appendSubmittedAiBuilderConfirmUserMessage = ({
  messages,
  messageId,
  requestId,
  retry,
  label,
  status = 'sending',
}: {
  messages: AiBuilderMessage[];
  messageId: string;
  requestId: number;
  retry?: AiBuilderUserMessageRetryRequest;
  label: string;
  status?: Extract<AiBuilderMessage, { kind: 'user' }>['status'];
}): AiBuilderMessage[] => {
  return [
    ...clearAiBuilderAssistantFollowupControls(messages).map(message =>
      message.id === messageId && message.kind === 'confirm'
        ? { ...message, submitted: true }
        : message,
    ),
    {
      id: createSubmittedAiBuilderConfirmUserMessageId(requestId),
      kind: 'user',
      content: label,
      status,
      ...(retry ? { retry } : {}),
    },
  ];
};

/**
 * 문제 카드 `AI로 생성` 진입 시 선택 문제 기준 후속 선택지(문제 다듬기/변형하기/
 * 유사 문제 만들기)를 로컬 choice 메시지로 덧붙인다.
 *
 * 제출은 기존 choice 흐름(submitChoiceMessage → message turn + selected
 * question context)을 그대로 타므로 서버 turn 계약과 호환된다. 마지막 메시지가
 * 아직 제출되지 않은 동일 슬롯 선택지면 중복으로 붙이지 않는다.
 */
export const appendQuizCardActionChoiceMessage = ({
  messages,
  copy,
}: {
  messages: AiBuilderMessage[];
  copy: AiBuilderChatCopy;
}): AiBuilderMessage[] => {
  const lastMessage = messages.at(-1);
  if (
    lastMessage?.kind === 'choice' &&
    !lastMessage.submitted &&
    lastMessage.missingSlot === QUIZ_CARD_ACTION_CHOICE_SLOT
  ) {
    return messages;
  }

  return [
    ...messages,
    {
      id: `quiz-card-choice-${createAiBuilderClientRequestId()}`,
      kind: 'choice',
      title: copy.quizCardChoice.title,
      missingSlot: QUIZ_CARD_ACTION_CHOICE_SLOT,
      selectionMode: 'single',
      options: [
        { id: 'refine', emoji: '✏️', label: copy.quizCardChoice.refineOption },
        {
          id: 'transform',
          emoji: '🔄',
          label: copy.quizCardChoice.transformOption,
        },
        {
          id: 'similar',
          emoji: '➕',
          label: copy.quizCardChoice.similarOption,
        },
      ],
      selectedOptionIds: [],
      submitted: false,
    },
  ];
};

export const isUnsubmittedQuizCardActionChoiceMessage = (
  message: AiBuilderMessage,
): message is Extract<AiBuilderMessage, { kind: 'choice' }> =>
  message.kind === 'choice' &&
  !message.submitted &&
  message.missingSlot === QUIZ_CARD_ACTION_CHOICE_SLOT;

export const removeUnsubmittedQuizCardActionChoiceMessages = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] => {
  let changed = false;
  const nextMessages = messages.filter(message => {
    const shouldRemove = isUnsubmittedQuizCardActionChoiceMessage(message);
    if (shouldRemove) {
      changed = true;
    }

    return !shouldRemove;
  });

  return changed ? nextMessages : messages;
};
